import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { createQRToken } from '../utils/crypto';
import jsPDF from 'jspdf';

// Array of fun emojis to randomly choose from
const EMOJIS = ['🔍', '🎯', '🧩', '🎲', '🎪', '🎨', '🎭', '🎬', '🎸', '🎹', '🎺', '🎻', '🎮', '🎰', '🎳', '🎯', '🏆', '🏅', '🎖️', '🎗️', '🎫', '🎟️', '🎠', '🎡', '🎢', '🎤', '🎧', '🎼', '🔮', '🧸', '🎁', '🎈', '🎉', '🎊', '🎀', '🔑', '🗝️', '🔐', '🔒', '🗿', '🏛️', '⚡', '🌟', '⭐', '✨', '💫', '🌈', '🦄', '🐉', '🦋', '🌺', '🌸', '🌼', '🌻', '🌹', '🏵️', '🍀', '🎄', '🌲', '🌳', '🎋', '🎍'];

const QRCodeGenerator: React.FC = () => {
  // Get the default base domain from environment variables or fallback
  const defaultBaseDomain = import.meta.env.VITE_BASE_DOMAIN || 'https://raetseljagd.tu-bs.de';
  
  const [selectedNumber, setSelectedNumber] = useState<string>('01');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState<string>(defaultBaseDomain);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [generatedCodes, setGeneratedCodes] = useState<Set<string>>(new Set());
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  
  useEffect(() => {
    // Load any saved generated codes from localStorage
    const savedCodes = localStorage.getItem('generatedQRCodes');
    if (savedCodes) {
      try {
        const codesArray = JSON.parse(savedCodes);
        setGeneratedCodes(new Set(codesArray));
      } catch (e) {
        console.error('Error parsing saved QR codes:', e);
      }
    }
  }, []);

  // Array of QR code numbers from 01 to 14 (15 and 16 are special challenges without QR codes)
  const qrNumbers = Array.from({ length: 14 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  // Get a random emoji
  const getRandomEmoji = (): string => {
    return EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
  };

  // Add text overlay to QR code image
  const addTextOverlay = (qrCodeImageUrl: string, number: string, emoji: string): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Set canvas size to match QR code
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Layer 1: Draw QR code base
        ctx!.drawImage(img, 0, 0);
        
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        
        // Layer 2: Add white background circle (slightly larger)
        const circleRadius = 40;
        ctx!.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx!.beginPath();
        ctx!.arc(centerX, centerY, circleRadius, 0, 2 * Math.PI);
        ctx!.fill();
        
        // Layer 3: Draw emoji (larger, slightly transparent)
        ctx!.globalAlpha = 0.65;
        ctx!.font = '50px Arial';
        ctx!.textAlign = 'center';
        ctx!.textBaseline = 'middle';
        ctx!.fillText(emoji, centerX, centerY);
        
        // Layer 4: Draw number in front (fully opaque)
        ctx!.globalAlpha = 1.0;
        ctx!.fillStyle = '#000000';
        ctx!.font = 'bold 24px Arial';
        ctx!.fillText(`#${number}`, centerX, centerY);
        
        // Convert canvas to data URL
        resolve(canvas.toDataURL('image/png'));
      };
      
      img.src = qrCodeImageUrl;
    });
  };

  // Generate a QR code
  const generateQRCode = async (number?: string) => {
    const targetNumber = number || selectedNumber;
    try {
      // Get a random emoji for this QR code
      const emoji = getRandomEmoji();
      
      // Create JWT token with the number and emoji
      const token = await createQRToken(targetNumber, emoji);
      
      if (!token) {
        setMessage({ type: 'error', text: 'Fehler beim Erstellen des Tokens. Bitte überprüfen Sie Ihre Schlüssel.' });
        return null;
      }
      
      // Normalize base URL by removing trailing slashes
      const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, '');
      
      // Create the full URL with the token in the fragment
      const scanPath = normalizedBaseUrl.endsWith('/scan') ? normalizedBaseUrl : `${normalizedBaseUrl}/scan`;
      const fullUrl = `${scanPath}#${token}`;
      
      // Generate QR code as data URL
      const baseQrCodeImageUrl = await QRCode.toDataURL(fullUrl, {
        errorCorrectionLevel: 'H',
        margin: 4,
        width: 300,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      // Add text overlay with number and emoji
      const qrCodeImageUrl = await addTextOverlay(baseQrCodeImageUrl, targetNumber, emoji);
      
      // Update UI only if this is a single generation (not part of bulk)
      if (!number) {
        setQrCodeUrl(qrCodeImageUrl);
        setGeneratedUrl(fullUrl);
        setMessage({ type: 'success', text: `QR-Code für Nummer ${targetNumber} ${emoji} erfolgreich generiert!` });
      }
      
      // Update generated codes and persist in localStorage
      const updatedCodes = new Set([...generatedCodes, targetNumber]);
      setGeneratedCodes(updatedCodes);
      localStorage.setItem('generatedQRCodes', JSON.stringify([...updatedCodes]));
      
      return { number: targetNumber, emoji, qrCodeImageUrl, fullUrl };
    } catch (error) {
      console.error('Error generating QR code:', error);
      if (!number) {
        setMessage({ type: 'error', text: 'Fehler beim Generieren des QR-Codes.' });
      }
      return null;
    }
  };

  // Generate and download all QR codes at once
  const generateAndDownloadAll = async () => {
    setIsGeneratingAll(true);
    setMessage({ type: 'success', text: 'Generiere alle QR-Codes...' });
    
    try {
      const allQRCodes: Array<{ number: string; emoji: string; qrCodeImageUrl: string; fullUrl: string }> = [];
      
      // Generate all QR codes
      for (const number of qrNumbers) {
        const result = await generateQRCode(number);
        if (result) {
          allQRCodes.push(result);
        }
        // Small delay to prevent overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Create a zip-like download by triggering multiple downloads
      // Or create a combined PDF/HTML page
      setMessage({ type: 'success', text: `Alle ${allQRCodes.length} QR-Codes generiert! Starte Download...` });
      
      // Download each QR code
      for (let i = 0; i < allQRCodes.length; i++) {
        const qr = allQRCodes[i];
        await new Promise(resolve => setTimeout(resolve, 200)); // Delay between downloads
        
        const link = document.createElement('a');
        link.href = qr.qrCodeImageUrl;
        link.download = `raetseljagd-qr-${qr.number}-${qr.emoji}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      setMessage({ type: 'success', text: `Alle ${allQRCodes.length} QR-Codes wurden heruntergeladen!` });
    } catch (error) {
      console.error('Error generating all QR codes:', error);
      setMessage({ type: 'error', text: 'Fehler beim Generieren aller QR-Codes.' });
    } finally {
      setIsGeneratingAll(false);
    }
  };

  // Generate all QR codes and export to PDF
  const generateAndDownloadPDF = async () => {
    setIsGeneratingAll(true);
    setMessage({ type: 'success', text: 'Generiere PDF mit allen QR-Codes...' });
    
    try {
      const allQRCodes: Array<{ number: string; emoji: string; qrCodeImageUrl: string; fullUrl: string }> = [];
      
      // Generate all QR codes
      for (const number of qrNumbers) {
        const result = await generateQRCode(number);
        if (result) {
          allQRCodes.push(result);
        }
        // Small delay to prevent overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (allQRCodes.length === 0) {
        throw new Error('No QR codes generated');
      }
      
      // Create PDF with A4 size (210 x 297 mm)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Layout: 2 QR codes per row, 4 rows per page (8 per page)
      // A4 dimensions: 210mm x 297mm
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const qrSize = 80; // Size of each QR code in mm
      const horizontalSpacing = (pageWidth - 2 * margin - 2 * qrSize) / 3; // Space between and around QR codes
      const verticalSpacing = (pageHeight - 2 * margin - 4 * qrSize) / 5; // Space between and around QR codes
      
      let currentPage = 1;
      let positionOnPage = 0;
      
      for (let i = 0; i < allQRCodes.length; i++) {
        const qr = allQRCodes[i];
        
        // Add new page if needed (after every 8 QR codes)
        if (i > 0 && i % 8 === 0) {
          pdf.addPage();
          currentPage++;
          positionOnPage = 0;
        }
        
        // Calculate position (2 per row, 4 rows)
        const col = positionOnPage % 2; // 0 or 1
        const row = Math.floor(positionOnPage / 2); // 0, 1, 2, or 3
        
        const x = margin + horizontalSpacing + col * (qrSize + horizontalSpacing);
        const y = margin + verticalSpacing + row * (qrSize + verticalSpacing);
        
        // Add QR code image to PDF
        pdf.addImage(qr.qrCodeImageUrl, 'PNG', x, y, qrSize, qrSize);
        
        // Add label below QR code
        pdf.setFontSize(10);
        pdf.text(`#${qr.number} ${qr.emoji}`, x + qrSize / 2, y + qrSize + 5, { align: 'center' });
        
        positionOnPage++;
      }
      
      // Save the PDF
      pdf.save('raetseljagd-qr-codes.pdf');
      
      setMessage({ type: 'success', text: `PDF mit ${allQRCodes.length} QR-Codes wurde heruntergeladen!` });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setMessage({ type: 'error', text: 'Fehler beim Generieren des PDFs.' });
    } finally {
      setIsGeneratingAll(false);
    }
  };

  // Download the QR code image
  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `raetseljagd-qr-${selectedNumber}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print the QR code
  const printQRCode = () => {
    if (!qrCodeUrl || !generatedUrl) return;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR-Code drucken</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
                color: black;
                background-color: white;
              }
              img {
                max-width: 80%;
                height: auto;
                border: 1px solid #ddd;
                padding: 10px;
                background: white;
              }
              h2 {
                margin-top: 20px;
                color: black;
              }
              .url-container {
                margin-top: 10px;
                max-width: 80%;
                word-break: break-all;
                font-family: monospace;
                font-size: 12px;
                text-align: center;
                color: #333;
              }
            </style>
          </head>
          <body>
            <img src="${qrCodeUrl}" />
            <h2>QR-Code: ${selectedNumber}</h2>
            <div class="url-container">${generatedUrl}</div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 500);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <div className="qr-generator">
      <h2>QR-Code Generator</h2>
      
      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button className="close-button" onClick={() => setMessage(null)}>×</button>
        </div>
      )}
      
      <div className="qr-form">
        <div className="form-group">
          <label htmlFor="baseUrl">Basis-Domain:</label>
          <div className="url-input-container">
            <input
              type="text"
              id="baseUrl"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://raetseljagd.tu-bs.de"
            />
            <span className="url-path-suffix">/scan</span>
          </div>
          <small className="form-help-text">Die Basis-Domain wird aus der Umgebungsvariable VITE_BASE_DOMAIN geladen.</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="qrNumber">QR-Code Nummer:</label>
          <select
            id="qrNumber"
            value={selectedNumber}
            onChange={(e) => setSelectedNumber(e.target.value)}
          >
            {qrNumbers.map(number => (
              <option 
                key={number} 
                value={number}
                style={generatedCodes.has(number) ? { fontWeight: 'bold', color: 'green' } : undefined}
              >
                {number} {generatedCodes.has(number) ? '(generiert)' : ''}
              </option>
            ))}
          </select>
        </div>
        
        <button className="primary-button" onClick={() => generateQRCode()}>
          QR-Code generieren
        </button>
        
        <button 
          className="secondary-button" 
          onClick={generateAndDownloadAll}
          disabled={isGeneratingAll}
          style={{ marginTop: '1rem' }}
        >
          {isGeneratingAll ? 'Generiere alle...' : 'Alle generieren und herunterladen'}
        </button>
        
        <button 
          className="secondary-button" 
          onClick={generateAndDownloadPDF}
          disabled={isGeneratingAll}
          style={{ marginTop: '1rem' }}
        >
          {isGeneratingAll ? 'Generiere PDF...' : 'Alle als PDF exportieren'}
        </button>
      </div>
      
      {qrCodeUrl && (
        <div className="qr-result">
          <div className="qr-image-container">
            <img src={qrCodeUrl} alt={`QR-Code ${selectedNumber}`} className="qr-image" />
            <p className="qr-number">QR-Code: {selectedNumber}</p>
          </div>
          
          <div className="qr-url-section">
            <h3>QR-Code URL</h3>
            <div className="qr-url">
              <div className="url-container">
                <input 
                  type="text" 
                  value={generatedUrl || ''} 
                  readOnly 
                  className="url-display"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button 
                  className="copy-button" 
                  onClick={() => {
                    if (generatedUrl) {
                      navigator.clipboard.writeText(generatedUrl);
                      setMessage({ type: 'success', text: 'URL in die Zwischenablage kopiert!' });
                      setTimeout(() => setMessage(null), 3000);
                    }
                  }}
                >
                  Kopieren
                </button>
              </div>
              <p className="url-help-text">
                Diese URL enthält einen JWT-Token im URL-Fragment (#). Der Token enthält die signierte QR-Code-Nummer.
              </p>
            </div>
            
            <div className="qr-code-preview">
              <p>Test-Link: <a href={generatedUrl || '#'} target="_blank" rel="noopener noreferrer">QR-Code testen</a></p>
            </div>
          </div>
          
          <div className="qr-actions">
            <button className="secondary-button" onClick={downloadQRCode}>
              Herunterladen
            </button>
            <button className="secondary-button" onClick={printQRCode}>
              Drucken
            </button>
          </div>
        </div>
      )}
      
      <div className="qr-status">
        <h3>Generierte QR-Codes</h3>
        <div className="qr-grid">
          {qrNumbers.map(number => (
            <div 
              key={number} 
              className={`qr-status-box ${generatedCodes.has(number) ? 'generated' : 'pending'}`}
              onClick={() => setSelectedNumber(number)}
            >
              {number}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;